import {
  SageHoursCostCodeRow,
  SageHoursEmployeeRow,
  SageHoursScope,
  SageJobSiteHoursResponse,
  SageJobSiteHoursSource,
  SageWorkDayHoursSource,
} from "../Interfaces"

export const UNASSIGNED_LABEL = "Unassigned"
export const EMPTY_DAY_HOURS_MESSAGE = "No hours for this day."
export const EMPTY_PROJECT_HOURS_MESSAGE = "No hours on this job site yet."
export const EMPTY_HOURS_MESSAGE = EMPTY_DAY_HOURS_MESSAGE
export const ALL_DAYS_ON_SITE_LABEL = "All days on this job site"
export const HOURS_SUBTITLE = "Job site · Hours"

export function emptyHoursMessage(scope: SageHoursScope = "day"): string {
  return scope === "project" ? EMPTY_PROJECT_HOURS_MESSAGE : EMPTY_DAY_HOURS_MESSAGE
}

export function parseHoursScope(value: string | null | undefined): SageHoursScope {
  return value === "project" ? "project" : "day"
}

export function formatHours(hours: number): string {
  const value = Number.isFinite(hours) ? hours : 0
  return value.toFixed(1)
}

export function roundHours(hours: number, places = 2): number {
  const factor = 10 ** places
  return Math.round((Number.isFinite(hours) ? hours : 0) * factor) / factor
}

export function sumHours(values: Array<{ hours: number }>): number {
  return roundHours(values.reduce((total, row) => total + (Number(row.hours) || 0), 0))
}

function hoursThenName(hoursA: number, hoursB: number, nameA: string, nameB: string): number {
  const hoursDiff = hoursB - hoursA
  if (hoursDiff !== 0) return hoursDiff
  return nameA.localeCompare(nameB, undefined, { sensitivity: "base" })
}

export function employeeDisplayName(row: Pick<SageHoursEmployeeRow, "employeeName" | "firstName" | "lastName" | "employeeId" | "employeeNum">): string {
  const combined = `${row.firstName || ""} ${row.lastName || ""}`.trim()
  return row.employeeName?.trim() || combined || (row.employeeNum ? `Employee ${row.employeeNum}` : `Employee ${row.employeeId}`)
}

export function costCodeDisplayName(row: SageHoursCostCodeRow): string {
  if (row.unassigned || row.costCodeId == null) return UNASSIGNED_LABEL
  return (row.name || row.code || `Cost code ${row.costCodeId}`).trim()
}

export function sortEmployeeHours(rows: SageHoursEmployeeRow[]): SageHoursEmployeeRow[] {
  return [...rows].sort((a, b) =>
    hoursThenName(a.hours, b.hours, employeeDisplayName(a), employeeDisplayName(b))
  )
}

export function sortCostCodeHours(rows: SageHoursCostCodeRow[]): SageHoursCostCodeRow[] {
  return [...rows].sort((a, b) =>
    hoursThenName(a.hours, b.hours, costCodeDisplayName(a), costCodeDisplayName(b))
  )
}

export function isHoursEmpty(summary: Pick<SageJobSiteHoursResponse, "byEmployee" | "byCostCode" | "totalHours">): boolean {
  return (summary.byEmployee?.length || 0) === 0
    && (summary.byCostCode?.length || 0) === 0
    && !summary.totalHours
}

/**
 * Ensure Unassigned appears on the cost-code pane when clocked time lacks a
 * cost code, so left/right pane totals equal the day total.
 */
export function reconcileDailyHours(summary: SageJobSiteHoursResponse): SageJobSiteHoursResponse {
  const employees = sortEmployeeHours(
    (summary.byEmployee || []).filter((row) => Number(row.hours) > 0)
  )
  const incomingCodes = (summary.byCostCode || []).filter((row) => Number(row.hours) > 0)

  const assignedRows = incomingCodes.filter((row) => !row.unassigned && row.costCodeId != null)
  const existingUnassigned = incomingCodes
    .filter((row) => row.unassigned || row.costCodeId == null)
    .reduce((total, row) => total + row.hours, 0)

  const employeeTotal = sumHours(employees)
  const assignedTotal = sumHours(assignedRows)
  const totalHours = roundHours(
    summary.totalHours || employeeTotal || assignedTotal + (summary.unassignedHours || existingUnassigned)
  )

  let unassignedHours = roundHours(
    summary.unassignedHours ?? existingUnassigned ?? Math.max(0, totalHours - assignedTotal)
  )
  if (unassignedHours <= 0) {
    unassignedHours = roundHours(Math.max(0, totalHours - assignedTotal))
  }

  const costCodes = [...assignedRows]
  if (unassignedHours > 0) {
    costCodes.push({
      costCodeId: null,
      sageCostCodeId: null,
      code: UNASSIGNED_LABEL,
      name: UNASSIGNED_LABEL,
      hours: unassignedHours,
      unassigned: true,
    })
  }

  return {
    ...summary,
    success: summary.success !== false,
    scope: summary.scope === "project" ? "project" : "day",
    totalHours,
    unassignedHours,
    byEmployee: employees,
    byCostCode: sortCostCodeHours(costCodes),
  }
}

function asNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

function asEmployeeRow(raw: Record<string, unknown>, index: number): SageHoursEmployeeRow {
  const sageEmployeeId = raw.sageEmployeeId ?? raw.employeeId ?? raw.id
  const firstName = raw.firstName != null ? String(raw.firstName) : undefined
  const lastName = raw.lastName != null ? String(raw.lastName) : undefined
  const employeeNum = raw.employeeNum != null ? asNumber(raw.employeeNum) : undefined
  const combined = `${firstName || ""} ${lastName || ""}`.trim()
  return {
    employeeId: asNumber(sageEmployeeId ?? index + 1),
    sageEmployeeId: sageEmployeeId != null ? asNumber(sageEmployeeId) : undefined,
    employeeNum,
    employeeName: String(raw.employeeName ?? raw.name ?? combined),
    firstName,
    lastName,
    hours: asNumber(raw.hours),
  }
}

function asCostCodeRow(raw: Record<string, unknown>): SageHoursCostCodeRow {
  const rawId = raw.sageCostCodeId ?? raw.costCodeId ?? raw.id
  const label = String(raw.name ?? raw.code ?? "")
  const unassigned = Boolean(raw.unassigned)
    || rawId == null
    || label.toLowerCase() === UNASSIGNED_LABEL.toLowerCase()
  return {
    costCodeId: unassigned || rawId == null ? null : asNumber(rawId) || null,
    sageCostCodeId: unassigned || rawId == null ? null : asNumber(rawId) || null,
    code: unassigned ? UNASSIGNED_LABEL : (raw.code != null ? String(raw.code) : null),
    name: unassigned ? UNASSIGNED_LABEL : (label || null),
    hours: asNumber(raw.hours),
    unassigned,
  }
}

function readArray(raw: Record<string, unknown>, keys: string[]): Record<string, unknown>[] {
  for (const key of keys) {
    const value = raw[key]
    if (Array.isArray(value)) return value as Record<string, unknown>[]
  }
  return []
}

/** Normalize SiteWalkAPI #129 hours payload: scope, totalHours, byEmployee, byCostCode. */
export function normalizeHoursResponse(
  raw: unknown,
  jobSiteId: number,
  date: string | null,
  source: SageJobSiteHoursSource,
  fallbackScope: SageHoursScope = "day",
): SageJobSiteHoursResponse {
  const body = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>
  const employees = readArray(body, ["byEmployee"]).map(asEmployeeRow)
  const costCodes = readArray(body, ["byCostCode"]).map((row) => asCostCodeRow(row))
  const scope = parseHoursScope(typeof body.scope === "string" ? body.scope : fallbackScope)

  return reconcileDailyHours({
    success: body.success !== false,
    jobSiteId: asNumber(body.jobSiteId) || jobSiteId,
    jobSiteName: body.jobSiteName != null ? String(body.jobSiteName) : undefined,
    scope,
    date: body.date === null ? null : (typeof body.date === "string" ? body.date : (scope === "project" ? null : date)),
    totalHours: asNumber(body.totalHours),
    unassignedHours: asNumber(body.unassignedHours),
    byEmployee: employees,
    byCostCode: costCodes,
    source,
    error: body.error != null ? String(body.error) : undefined,
  })
}

export function emptyHoursResponse(
  jobSiteId: number,
  date: string | null,
  source: SageJobSiteHoursSource = "unavailable",
  scope: SageHoursScope = "day",
): SageJobSiteHoursResponse {
  return {
    success: true,
    jobSiteId,
    scope,
    date: scope === "project" ? null : date,
    totalHours: 0,
    unassignedHours: 0,
    byEmployee: [],
    byCostCode: [],
    source,
  }
}

function hoursBetween(start?: string | Date | null, end?: string | Date | null, now = Date.now()): number {
  if (!start) return 0
  const startMs = new Date(start).getTime()
  const endMs = end ? new Date(end).getTime() : now
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 0
  return (endMs - startMs) / 3_600_000
}

function segmentHours(detail: NonNullable<SageWorkDayHoursSource["details"]>[number], now: number): number {
  if (detail.hours != null && Number.isFinite(Number(detail.hours))) {
    return Number(detail.hours)
  }
  return hoursBetween(detail.startedAt, detail.endedAt, now)
}

function employeeNameFromWorkDay(workDay: SageWorkDayHoursSource): string {
  const person = workDay.employee
  const name = `${person?.firstName || ""} ${person?.lastName || ""}`.trim()
  if (name) return name
  if (person?.employeeNum) return `Employee ${person.employeeNum}`
  return `Employee ${workDay.sageEmployeeId || ""}`.trim()
}

/**
 * Aggregate date-scoped work-day rows into the hours summary contract.
 * Used when GET .../hours is not available yet. Open segments without
 * persisted hours use elapsed time through `now`.
 */
export function aggregateHoursFromWorkDays(
  workDays: SageWorkDayHoursSource[] | undefined,
  jobSiteId: number,
  date: string | null,
  now = Date.now(),
  scope: SageHoursScope = "day",
): SageJobSiteHoursResponse {
  const employees = new Map<number, SageHoursEmployeeRow>()
  const costCodes = new Map<string, SageHoursCostCodeRow>()
  let unassignedHours = 0

  for (const workDay of workDays || []) {
    if (workDay.sageJobSiteId != null && workDay.sageJobSiteId !== jobSiteId) continue

    const details = workDay.details || []
    let assigned = 0
    let detailUnassigned = 0
    for (const detail of details) {
      const hours = segmentHours(detail, now)
      if (hours <= 0) continue
      const hasCode = detail.sageCostCodeId != null || detail.costCode?.id != null
      if (!hasCode) {
        detailUnassigned += hours
        continue
      }
      assigned += hours
      const id = detail.costCode?.id ?? detail.sageCostCodeId as number
      const key = String(id)
      const existing = costCodes.get(key)
      const nextHours = (existing?.hours || 0) + hours
      costCodes.set(key, {
        costCodeId: id,
        code: detail.costCode?.code ?? existing?.code ?? null,
        name: detail.costCode?.name ?? existing?.name ?? null,
        hours: nextHours,
      })
    }

    const clocked = hoursBetween(workDay.clockedInAt, workDay.clockedOutAt, now)
    const leftover = Math.max(0, clocked - assigned - detailUnassigned)
    const employeeUnassigned = detailUnassigned + leftover
    unassignedHours += employeeUnassigned
    const employeeHours = assigned + employeeUnassigned

    if (employeeHours <= 0) continue

    const employeeId = workDay.employee?.id ?? workDay.sageEmployeeId ?? workDay.id ?? employees.size + 1
    const existing = employees.get(employeeId)
    employees.set(employeeId, {
      employeeId,
      employeeName: existing?.employeeName || employeeNameFromWorkDay(workDay),
      firstName: workDay.employee?.firstName,
      lastName: workDay.employee?.lastName,
      hours: (existing?.hours || 0) + employeeHours,
    })
  }

  return reconcileDailyHours({
    success: true,
    jobSiteId,
    scope,
    date: scope === "project" ? null : date,
    totalHours: 0,
    unassignedHours,
    byEmployee: Array.from(employees.values()),
    byCostCode: Array.from(costCodes.values()),
    source: "work-days-fallback",
  })
}
